import React, { useState } from 'react';
import {
	Control,
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';

import {
	Button,
	GridContainer,
	Input,
	InputType,
	Modal,
	Select,
} from '@components/common';
import Logo from '@components/ModalComponents/OrganisationForm/BasicInfo/Logo';
import UploadLogo from '@components/ModalComponents/OrganisationForm/BasicInfo/UploadLogo';
import { TCreateUploadUrl } from '@components/ModalComponents/OrganisationForm/BasicInfo/UploadLogo/UploadLogo';

import { useUploadLogo } from '@hooks/useUploadLogo';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { TCheckUniqOrganisations } from '@interfaces/components/form';

import { MODAL_WIDTH_580 } from '@constants/modals';
import { regExpNumbersAndLetters } from '@constants/regExp';
import { basicInfoAreas, defaultValuesCheckErrors, fieldsToTestBasicInfo, tabsNames } from '../constants';

import styles from './BasicInfo.module.scss';

interface IBasicInfoProps {
	setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
	isEditMode: boolean;
	checkUniqueValuesOrganisations: (params: {
		name: string;
		legalName: string;
		email?: string | null;
		organizationId?: string | null;
		successHandler: () => void;
		errorHandler: React.Dispatch<TCheckUniqOrganisations>;
	}) => void;
	organisationCheckErrors: TCheckUniqOrganisations;
	setOrganisationCheckErrors: React.Dispatch<React.SetStateAction<TCheckUniqOrganisations>>;
	register: UseFormRegister<FieldValues>;
	errors: FieldErrors;
	setValue: UseFormSetValue<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	trigger: UseFormTrigger<FieldValues>;
	isSettings: boolean;
	control: Control;
	timezones: Array<TSelectOption>;
	isBasicInfoTabError: boolean;
}

const BasicInfo = ({
	checkUniqueValuesOrganisations,
	control,
	errors,
	isEditMode,
	isSettings,
	organisationCheckErrors,
	register,
	setCurrentTab,
	setOrganisationCheckErrors,
	setValue,
	timezones,
	trigger,
	watch,
	isBasicInfoTabError,
}: IBasicInfoProps) => {
	const avatarId = watch('avatarId');
	const contactNumber = watch('contactNumber') ? `+${watch('contactNumber')}` : '';

	const [isOpenLogoModal, setIsOpenLogoModal] = useState(false);

	const toggleLogoModal = () => setIsOpenLogoModal(prevState => !prevState);

	const setLogoId = (id: string) => setValue('avatarId', id);

	const removeLogo = () => setLogoId('');

	const {
		createUploadLogoUrl,
		progress,
		isLoadingCreateUploadLogoUrl,
	} = useUploadLogo({ setLogoId, toggleLogoModal });

	const resetNameError = () =>
		setOrganisationCheckErrors(prevState => ({
			...prevState,
			nameError: defaultValuesCheckErrors.nameError,
		}));

	const resetLegalNameError = () =>
		setOrganisationCheckErrors(prevState => ({
			...prevState,
			legalNameError: defaultValuesCheckErrors.legalNameError,
		}));

	const resetEmailError = () =>
		setOrganisationCheckErrors(prevState => ({
			...prevState,
			emailError: defaultValuesCheckErrors.emailError,
		}));

	const onSubmit = async () => {
		const isValid = await trigger(fieldsToTestBasicInfo);

		if (!isValid) return;

		if (!isEditMode) {
			const switchToNext = () => setCurrentTab(tabsNames.address);

			checkUniqueValuesOrganisations({
				name: watch('name'),
				legalName: watch('legalName'),
				email: watch('email') || null,
				successHandler: switchToNext,
				errorHandler: setOrganisationCheckErrors,
			});
		}
	};

	const onChangeContactNumber = (phone: string) => {
		const formattedPhone = phone.slice(1);

		setValue('contactNumber', formattedPhone);
	};

	const handleBlurContactNumber = () => trigger('contactNumber');

	return (
		<div>
			<Modal
				isOpen={isOpenLogoModal}
				isScrollableContent
				onClose={toggleLogoModal}
				title='Upload image'
				width={MODAL_WIDTH_580}
			>
				<UploadLogo
					createUploadUrl={createUploadLogoUrl as unknown as TCreateUploadUrl}
					isLoadingCreateUploadUrl={isLoadingCreateUploadLogoUrl}
					progress={progress}
					toggleLogoModal={toggleLogoModal}
				/>
			</Modal>
			<GridContainer
				columns={2}
				customClassName={styles.basicInfoFormContainer}
				gap='15px 20px'
				gridTemplateAreas={basicInfoAreas(isSettings)}
			>
				<div className={styles.name}>
					<Input
						errorMessage={(errors?.name?.message || organisationCheckErrors.nameError)}
						label='Name'
						name='name'
						onFocus={resetNameError}
						placeholder='Enter name'
						register={register}
						required
					/>
				</div>
				<div className={styles.legalName}>
					<Input
						errorMessage={(errors?.legalName?.message || organisationCheckErrors.legalNameError) as string}
						label='Legal Name'
						name='legalName'
						onFocus={resetLegalNameError}
						placeholder='Enter legal name'
						register={register}
						required
					/>
				</div>
				<div className={styles.logo}>
					<Logo
						avatarId={avatarId}
						openLogoModal={toggleLogoModal}
						removeLogo={removeLogo}
					/>
				</div>
				<div className={styles.website}>
					<Input
						errorMessage={errors?.url?.message}
						label='Website URL'
						name='url'
						placeholder='Enter website URL'
						register={register}
						type={InputType.url}
					/>
				</div>
				<div className={styles.email}>
					<Input
						errorMessage={(errors?.email?.message || organisationCheckErrors.emailError)}
						label='Contact Email'
						name='email'
						onFocus={resetEmailError}
						placeholder='Enter contact email'
						register={register}
						type={InputType.email}
					/>
				</div>
				<div className={styles.contactNumber}>
					<Input
						errorMessage={errors?.contactNumber?.message}
						isPhoneNumber
						label='Contact Number'
						name='contactNumber'
						onBlur={handleBlurContactNumber}
						onChange={onChangeContactNumber}
						placeholder='Enter contact number'
						value={contactNumber}
					/>
				</div>
				<div className={styles.companyNumber}>
					<Input
						errorMessage={errors?.companyNumber?.message}
						label='Company Registration Number'
						name='companyNumber'
						placeholder='Enter CRN'
						regexValidation={regExpNumbersAndLetters}
						register={register}
						setValue={setValue}
					/>
				</div>
				{!isSettings
					&& <div className={styles.timezone}>
						<Select
							control={control}
							errorMessage={errors.timezone?.message}
							isError={!!errors?.timezone}
							label='Timezone'
							menuPlacement='top'
							name='timezone'
							options={timezones}
							placeholder='Select timezone'
							required
							width='320px'
						/>
					</div>}
				{!isEditMode
					&& <div className={styles.submitButton}>
						<Button
							disabled={isBasicInfoTabError}
							onClick={onSubmit}
							text='Next'
						/>
					</div>
				}
			</GridContainer>
		</div>
	);
};

export default BasicInfo;
